import React, { useEffect, useState } from 'react';
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { Link } from 'react-router-dom';
import { getApiErrorMessage, handleApiCall } from '../../apis';
import { endpoint } from '../../utils/endpoints';
import PermissionGate from '../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../utils/permissions';
import EditIcon from '../../assets/image/edit-icon.svg'
import DeleteIcon from '../../assets/image/delete-icon.svg'
import LeftAngle from '../../assets/image/left-angle.svg'
import RightAngle from '../../assets/image/right-angle.svg'
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const pickSubAdminList = (d) => {
    if (!d || typeof d !== 'object') return [];
    const raw =
        d.sub_admin_data ??
        d.subAdmin_data ??
        d.sub_admins ??
        d.sub_admin_list ??
        (Array.isArray(d.data) ? d.data : null) ??
        (Array.isArray(d) ? d : null);
    return Array.isArray(raw) ? raw : [];
};

const formatLocations = (r) => {
    const locField =
        r?.mipl_location_id ??
        r?.mipl_locations ??
        r?.locations ??
        r?.location ??
        r?.company_location;
    if (Array.isArray(locField)) {
        const s = locField
            .map((x) =>
                x && typeof x === 'object'
                    ? x.name ?? x.location_name ?? x.title
                    : x
            )
            .filter(Boolean)
            .join(', ');
        return s || '—';
    }
    if (typeof locField === 'string' && locField.trim()) return locField.trim();
    return '—';
};

const formatModules = (r) => {
    const arr =
        r?.module_access ??
        r?.module_data ??
        r?.modules ??
        r?.module_list;
    if (Array.isArray(arr)) {
        const s = arr
            .map((m) =>
                m && typeof m === 'object'
                    ? m.module_name ?? m.name ?? m.title
                    : m
            )
            .filter(Boolean)
            .join(', ');
        if (s) return s;
    }
    const single =
        r?.module_name ??
        r?.module_access_summary ??
        r?.access_summary;
    if (typeof single === 'string' && single.trim()) return single.trim();
    const locStr = formatLocations(r);
    return locStr !== '—' ? locStr : '—';
};

const formatPermissions = (r) => {
    if (typeof r?.permission_access === 'string' && r.permission_access.trim()) {
        return r.permission_access.trim();
    }
    if (typeof r?.permission_summary === 'string' && r.permission_summary.trim()) {
        return r.permission_summary.trim();
    }
    if (typeof r?.privilege_summary === 'string' && r.privilege_summary.trim()) {
        return r.privilege_summary.trim();
    }
    const perms = r?.permissions ?? r?.privileges;
    if (Array.isArray(perms) && perms.length) {
        const parts = perms.map((p) => {
            if (!p || typeof p !== 'object') return String(p);
            const mod = p.module_name ?? p.module_id?.module_name ?? '';
            const privArr = p.privilege_type_id ?? p.privilege_id ?? [];
            const privStr = Array.isArray(privArr)
                ? privArr
                      .map((x) =>
                          x && typeof x === 'object'
                              ? x.privilege_type ?? x.name
                              : x
                      )
                      .filter(Boolean)
                      .join(', ')
                : '';
            return [mod, privStr].filter(Boolean).join(': ');
        });
        return parts.filter(Boolean).join('; ') || '—';
    }
    return '—';
};

const mapSubAdminRow = (r) => {
    const id = r?._id ?? r?.id ?? '';
    const name =
        r?.full_name ??
        r?.name ??
        r?.sub_admin_name ??
        r?.employee_name ??
        '—';
    const st = r?.status;
    return {
        id: id ? String(id) : '',
        adminName: name,
        moduleAccesses: formatModules(r),
        permissionAccess: formatPermissions(r),
        statusCode: st === 1 ? 1 : 0,
        status:
            st === 1
                ? 'Active'
                : st === 0
                  ? 'Inactive'
                  : typeof st === 'string'
                    ? st
                    : '—',
        /** Full API row — passed to Edit for prefill (no separate detail API) */
        record: r,
    };
};

const SubAdminManagement = () => {
    const [rows, setRows] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteId, setDeleteId] = useState(null);
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);

    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const searchForApi = (() => {
        const s = clampSearchInput(debouncedSearch).trim();
        if (!s) return '';
        if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
        return s;
    })();


    const fetchSubAdminList = async () => {
        setListLoading(true);
        try {
            const response = await handleApiCall(
                'get',
                `${endpoint.listOfSubAdmin}?search=${encodeURIComponent(searchForApi)}&page=${currentPage}&limit=${itemsPerPage}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data ?? {};
                const list = pickSubAdminList(d);
                const mapped = list.map(mapSubAdminRow).filter((row) => row.id);
                setRows(mapped);
                setTotalRecords(d?.total ?? mapped.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                // const errMsg =
                //     response?.response?.data?.errorMessage ||
                //     response?.response?.data?.message ||
                //     response?.data?.message ||
                //     'Failed to load sub admins';
                // notifyError(errMsg);
                notifyError(getApiErrorMessage(response, 'Failed to load sub admins'));
                setRows([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch {
            notifyError('Failed to load sub admins');
            setRows([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        fetchSubAdminList();
    }, [debouncedSearch, currentPage, itemsPerPage]);

    const handleSubAdminStatusToggle = async (subAdminId, currentStatus, record) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!subAdminId) return;
        setStatusUpdatingId(subAdminId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusSubAdmin, {
                sub_admin_id: subAdminId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === subAdminId
                            ? {
                                  ...r,
                                  statusCode: nextStatus,
                                  status: nextStatus === 1 ? 'Active' : 'Inactive',
                                  record:
                                      record && typeof record === 'object'
                                          ? { ...record, status: nextStatus }
                                          : r.record,
                              }
                            : r
                    )
                );
                notifySuccess(response?.data?.message || 'Status updated');
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Could not update status';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const handleDelete = () => {
        setRows((prev) => prev.filter((r) => r.id !== deleteId));
        setDeleteId(null);
    };

    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content mt-2.5 md:mt-7">

                <div className='flex gap-x-2.5 md:mb-4 mb-2.5'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.SUB_ADMIN} action="add">
                        <Link className="btn btn-danger ml-auto max-[767px]:text-[10px] max-[767px]:!px-2" to="/sub-admin-management/create-sub-admin">
                            + Create Sub Admin
                        </Link>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>Sub-Admin Name</th>
                                    <th>Module Accesses</th>
                                    {/* <th>Permission Access</th> */}
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!listLoading &&
                                    rows.map((driver, index) => (
                                    <tr key={driver.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{driver.adminName}</td>
                                        <td>{driver.moduleAccesses}</td>
                                        {/* <td>{driver.permissionAccess}</td> */}
                                        <td>{driver.status}</td>
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.SUB_ADMIN} action="edit">
                                                    <label className="relative inline-flex items-center cursor-pointer min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={driver.statusCode === 1}
                                                            onChange={() =>
                                                                handleSubAdminStatusToggle(
                                                                    driver.id,
                                                                    driver.statusCode,
                                                                    driver.record
                                                                )
                                                            }
                                                            disabled={statusUpdatingId === driver.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all" />
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.SUB_ADMIN} action="edit">
                                                    <Link
                                                        to={`/sub-admin-management/edit-sub-admin?sub_admin_id=${encodeURIComponent(driver.id)}`}
                                                        state={{ subAdminRecord: driver.record }}
                                                    >
                                                        <img src={EditIcon} alt="" className='h-7 min-w-[22px]'/>
                                                    </Link>
                                                </PermissionGate>
                                                {/* <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setDeleteId(driver.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setDeleteId(driver.id);
                                                        }
                                                    }}
                                                >
                                                    <img src={DeleteIcon} alt="" className='h-7 min-w-[22px]'/>
                                                </span> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-500">
                                            No sub admins found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 📄 Pagination */}
                <div className="flex gap-x-4 items-center mt-4">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    {/* Total Records */}
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                              ? '0 records found'
                              : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} alt="" className='invert grayscale brightness-100'/>
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center cursor-pointer"
                        >
                            <img src={RightAngle} alt="" className='invert grayscale brightness-100'/>
                        </button>

                    </div>
                </div>

            </div>

            {/* 🗑️ Delete Modal */}
            {deleteId && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <div className="bg-white p-6 rounded-xl">
                        <p className="mb-4 text-center font-bold">
                            Are you sure you want to delete?
                        </p>

                        <div className="flex justify-center gap-4">
                            <button
                                type="button"
                                className="border px-4 py-2 rounded"
                                onClick={() => setDeleteId(null)}
                            >
                                No
                            </button>

                            <button
                                type="button"
                                className="bg-red-500 text-white px-4 py-2 rounded"
                                onClick={handleDelete}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SubAdminManagement;
